import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { LinkContainer } from "react-router-bootstrap";
import { Col } from "react-bootstrap";

import moment from "moment";

import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";

import * as modalActions from "../../../actions/modalActions";
import * as callActions from "../../../actions/callActions";
import * as userActions from "../../../actions/userActions";
import * as contractActions from "../../../actions/contractActions";
import * as projectActions from "../../../actions/projectActions";
import * as customerActions from "../../../actions/customerActions";
import * as serviceActions from "../../../actions/serviceActions";
import * as loaderActions from "../../../actions/loaderActions";
import * as supportTicketActions from "../../../actions/supportTicketActions";
import { customToast as toast } from "../../../utils";
import TimesheetForm from "./TimesheetForm";
import BreadCrumbs from "../../common/BreadCrumbs";
import TimesheetFilters from "./TimesheetFilters";
import ConfirmModal from "../../common/ConfirmModal";
import Checkbox from "../../common/Checkbox";
import "./TimesheetPage.scss";

const TimesheetPage = ({
  customers,
  contracts,
  projects,
  users,
  range,
  codes,
  services,
  savingCall,
  auth,
  loading,
  actions,
  history,
  isMobile
}) => {
  const [calls, setCalls] = useState(null);
  const [showDate, setShowDate] = useState(false);
  const [user, setUser] = useState({
    value: auth.user.id,
    label: auth.user.full_name
  });

  const [ticketResolveState, setTicketResolveState] = useState({ note: "" });
  const [supportTicket, setSupportTicket] = useState({});
  const [sendCustomerEmail, setSendCustomerEmail] = useState(true);

  const [start, setStart] = useState(moment(new Date()).startOf("isoWeek"));
  const [needFetch, setNeedsFetch] = useState(true);
  const [needFetchData, setNeedFetchData] = useState(true);

  const [period, setPeriod] = useState(
    `${moment(start).startOf("isoWeek").format("MM/DD/YYYY")} - ${moment(start)
      .endOf("isoWeek")
      .format("MM/DD/YYYY")}`
  );

  const fetchCalls = useCallback(async () => {
    actions.toggleLoader(true);
    setNeedsFetch(false);
    const result = await actions.loadCalls({
      filtered: {
        assigned_to: [user],
        arrival_date: [
          moment(start).startOf("isoWeek").toDate(),
          moment(start).endOf("isoWeek").toDate()
        ]
      },
      isPopulated: true
    });

    setCalls(result.calls || []);
  }, [actions, start, user]);

  const fetchData = useCallback(async () => {
    if (!customers.length) actions.loadCustomers();
    if (!projects.length) actions.loadProjects();
    if (!services.length) actions.loadServices();
    if (!users.length) actions.loadUsers();
    if (!contracts.length) actions.loadContracts();
    actions.getDateRange(period);
  }, [
    actions,
    contracts.length,
    customers.length,
    period,
    projects.length,
    services.length,
    users.length
  ]);

  useEffect(() => {
    if (needFetchData && !needFetch && !loading) {
      setNeedFetchData(false);
      fetchData();
      if (isMobile) history.push("/app/calls/add");
    }
    if (needFetch && !loading) fetchCalls();
  }, [
    actions,
    contracts.length,
    customers.length,
    fetchCalls,
    fetchData,
    history,
    isMobile,
    loading,
    needFetch,
    needFetchData,
    period,
    projects.length,
    range.length,
    services.length,
    users.length
  ]);

  const handleChangeUser = (newUser) => {
    setUser(newUser);
    setNeedsFetch(true);
  };

  const handleChangeDate = ({ startDate }) => {
    const newStart = moment(startDate).startOf("isoWeek");
    const newEnd = moment(startDate).endOf("isoWeek");

    const newPeriod = `${moment(newStart).format("MM/DD/YYYY")} - ${moment(
      newEnd
    ).format("MM/DD/YYYY")}`;

    actions.getDateRange(newPeriod);
    setPeriod(newPeriod);
    setStart(newStart);
    setNeedsFetch(true);
  };

  const handleEnterCallDetails = () => {
    actions.showModal("callDetailsModal");
  };

  const handleOpenProjectDetails = () => {
    actions.showModalSuccess("projectDetailsModal");
  };

  const handleSave = async (callsToUpdate, ticketId = null) => {
    await Promise.all(
      callsToUpdate.map(async (call, i) => {
        try {
          const shouldAlert = i === callsToUpdate.length - 1;
          if (call._id) {
            if (call.actual_hrs === "") {
              await actions.deleteCall(call._id, "TIMESHEET", shouldAlert);
            } else if (call.changed) {
              await actions.updateCall(call, "TIMESHEET", shouldAlert);
              if (ticketId) {
                const { isResolvable, supportTicketObject } =
                  await actions.getIsTicketResolvable(ticketId);
                if (supportTicketObject && isResolvable) {
                  setSupportTicket(supportTicketObject);
                  actions.showModalSuccess("resolveTicketModal");
                }
              }
            }
          } else {
            const project = projects.find((p) => p._id === call.project);
            if (
              project &&
              project.project_status &&
              project.project_status.label === "Ready To Close"
            ) {
              throw new Error(
                `${project.name}'s status has been changed to Ready To Close. No additional calls can be added.`
              );
            }
            if (project && project.name && project.name === "Personal Time") {
              throw new Error(
                `Please submit a PTO Request to enter personal time.`
              );
            }
            await actions.createCall(call, "TIMESHEET", shouldAlert);
          }
        } catch (err) {
          toast.error(err.message);
        }
      })
    );
    await setNeedsFetch(true);
  };

  // Ticket resolution
  const handleInput = (e) => {
    const { value } = e.target;
    setTicketResolveState((prev) => ({ ...prev, note: value }));
  };

  const handleConfirmResolve = async () => {
    const resolvedStatus = codes.find(
      (code) =>
        code.entity === "Support Ticket" &&
        code.value === "R" &&
        code.field === "status"
    );

    const resolution = {
      resolved_by: auth.user._id,
      closing_note: ticketResolveState.note
    };

    const data = {
      ...supportTicket,
      request_status: resolvedStatus._id,
      resolution,
      modified_by_user_id: auth.user._id,
      additional_note: ticketResolveState.note,
      send_customer_email: sendCustomerEmail
    };

    await actions.updateSupportTicket(data);
    actions.hideModal();
  };

  const handleCancelResolve = () => {
    actions.hideModal();
  };

  const handleCheckbox = (e) => {
    setSendCustomerEmail(e?.target?.checked);
  };

  const confirmResolve = (
    <div>
      <p>{`Would you like to resolve Ticket # ${supportTicket.support_ticket_number}?`}</p>
      <textarea
        maxLength="250"
        className="form-control"
        onChange={handleInput}
        placeholder="Note"
      />
      <small
        style={{
          float: "right",
          color:
            ticketResolveState?.note && ticketResolveState.note.length >= 250
              ? "red"
              : ""
        }}
      >
        {ticketResolveState?.note ? `${ticketResolveState.note.length} / ` : ""}
        250 Character Limit
      </small>
      <div style={{ display: "flex", alignItems: "center", marginTop: "2rem" }}>
        <Checkbox
          name="sendCustomerEmail"
          checked={sendCustomerEmail}
          onClickCheck={handleCheckbox}
        />
        <div style={{ marginLeft: "1rem" }}>
          Send Customer Email
          <small style={{ marginLeft: "1rem" }}>
            *This includes the resolution note above.
          </small>
        </div>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <div className="header-wrapper">
        <BreadCrumbs breadcrumbs={[{ label: "Timesheet" }]} />
        <Col md={5} xs={12}>
          <TimesheetFilters
            user={user}
            users={users}
            period={period}
            range={range}
            showDate={showDate}
            onFocus={() => setShowDate(!showDate)}
            onChangeUser={handleChangeUser}
            onChangeDate={handleChangeDate}
            auth={auth}
          />
        </Col>
        <Col>
          <LinkContainer to="/app/calls/add">
            <button className="btn btn-success pull-right">New Call</button>
          </LinkContainer>
        </Col>
      </div>
      <div className="content-wrapper">
        <TimesheetForm
          calls={calls || []}
          customers={customers}
          contracts={contracts}
          projects={projects.filter(
            (p) =>
              p.project_status?.label === "In Process" ||
              p.project_status?.label === "Ready To Close"
          )}
          services={services}
          users={users}
          dates={range}
          user={user.value}
          codes={codes}
          auth={auth}
          saving={savingCall}
          loadingData={loading}
          onSave={handleSave}
          onCancel={history.goBack}
          deleteCall={actions.deleteCall}
          showModalSuccess={actions.showModalSuccess}
          onEnterCallDetails={handleEnterCallDetails}
          onShowProjectDetails={handleOpenProjectDetails}
          closeModal={actions.hideModal}
          updateCall={actions.getCallSuccess}
        />
      </div>
      <ConfirmModal
        id="resolveTicketModal"
        title="Resolve Ticket"
        body={confirmResolve}
        close={handleCancelResolve}
        confirm={handleConfirmResolve}
        cancelLabel="No"
      />
    </React.Fragment>
  );
};

TimesheetPage.propTypes = {
  actions: PropTypes.object.isRequired,
  savingCall: PropTypes.bool,
  customers: PropTypes.array,
  users: PropTypes.array,
  contracts: PropTypes.array,
  auth: PropTypes.object,
  codes: PropTypes.array,
  user: PropTypes.string,
  history: PropTypes.object,
  services: PropTypes.array,
  range: PropTypes.array,
  loading: PropTypes.bool,
  projects: PropTypes.array,
  isMobile: PropTypes.bool
};

function mapStatesToProps(state) {
  return {
    state: state.reducers,
    auth: state.reducers.auth,
    customers: state.reducers.customers,
    projects: state.reducers.projects,
    savingCall: state.reducers.savingCall,
    users: state.reducers.users,
    contracts: state.reducers.contracts,
    codes: state.reducers.codes,
    services: state.reducers.services,
    range: state.reducers.range,
    loading: state.reducers.loading
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...modalActions,
        ...callActions,
        ...userActions,
        ...contractActions,
        ...serviceActions,
        ...projectActions,
        ...customerActions,
        ...loaderActions,
        ...supportTicketActions
      },
      dispatch
    )
  };
}

export default connect(mapStatesToProps, mapDispatchToProps)(TimesheetPage);
